import { useState } from 'react';
import { useLoaderData } from 'react-router';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import userApi from '@/api/user';

const errorMessages = {
  invalid_password: 'Your current password is incorrect.',
  invalid_totp: 'That authenticator code is invalid or has already been used.',
  totp_throttled: 'Too many attempts. Wait a moment before trying another code.',
  mfa_setup_expired: 'This setup has expired. Start again.',
  mfa_setup_missing: 'No pending authenticator setup was found. Start again.',
};

const errorMessage = error =>
  errorMessages[error?.response?.data?.code] || error?.response?.data?.detail || 'Something went wrong. Try again.';

const CodeInput = ({ id, label, value, onChange, autoFocus = false }) => (
  <label className="form-control w-full max-w-xs" htmlFor={id}>
    <span className="label-text mb-2">{label}</span>
    <input
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      required
      autoFocus={autoFocus}
      data-sentry-mask
      value={value}
      onChange={event => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
      className="input input-bordered w-full"
    />
  </label>
);

CodeInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
};

const MfaRoute = () => {
  const user = useLoaderData();
  const [mfaEnabled, setMfaEnabled] = useState(user.mfa_enabled);
  const [action, setAction] = useState(null);
  const [provisioning, setProvisioning] = useState(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const cancel = () => {
    setAction(null);
    setProvisioning(null);
    setPassword('');
    setCode('');
    setError('');
  };

  const startSetup = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await userApi.setupMfa({ current_password: password });
      setProvisioning(data);
      setPassword('');
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await userApi.confirmMfa({ totp_code: code });
      setMfaEnabled(true);
      cancel();
      toast.success('Multi-factor authentication is now enabled.');
    } catch (requestError) {
      setError(errorMessage(requestError));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await userApi.disableMfa({ current_password: password, totp_code: code });
      setMfaEnabled(false);
      cancel();
      toast.success('Multi-factor authentication has been disabled.');
    } catch (requestError) {
      setError(errorMessage(requestError));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-xl">
      <p className="mb-4">
        MFA is currently <strong>{mfaEnabled ? 'enabled' : 'disabled'}</strong>.
      </p>

      {error && <p className="text-error mb-4">{error}</p>}

      {!action && mfaEnabled && (
        <div>
          <p className="text-sm mb-3">To change authenticators, disable MFA and then enable it again.</p>
          <button type="button" onClick={() => setAction('disable')} className="btn btn-error btn-sm">
            Disable MFA
          </button>
        </div>
      )}

      {!action && !mfaEnabled && (
        <div>
          <button type="button" onClick={() => setAction('enable')} className="btn btn-accent btn-sm">
            Enable MFA
          </button>
        </div>
      )}

      {action === 'enable' && !provisioning && (
        <form onSubmit={startSetup} className="space-y-4">
          <label className="form-control w-full max-w-sm" htmlFor="mfa-current-password">
            <span className="label-text mb-2">Current password</span>
            <input
              id="mfa-current-password"
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              data-sentry-mask
              value={password}
              onChange={event => setPassword(event.target.value)}
              className="input input-bordered w-full"
            />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="btn btn-accent btn-sm">
              {busy ? 'Continuing...' : 'Continue'}
            </button>
            <button type="button" onClick={cancel} disabled={busy} className="btn btn-ghost btn-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      {action === 'enable' && provisioning && (
        <form onSubmit={confirmSetup} className="space-y-4">
          <p>Scan this QR code with your authenticator app:</p>
          <div data-sentry-block>
            <QRCodeSVG value={provisioning.provisioning_uri} size={180} />
          </div>
          <p className="text-sm">Or enter this secret manually:</p>
          <code className="block break-all" data-sentry-mask>
            {provisioning.manual_secret}
          </code>
          <CodeInput id="mfa-confirmation-code" label="Authenticator code" value={code} onChange={setCode} autoFocus />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="btn btn-accent btn-sm">
              {busy ? 'Enabling...' : 'Enable MFA'}
            </button>
            <button type="button" onClick={cancel} disabled={busy} className="btn btn-ghost btn-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      {action === 'disable' && (
        <form onSubmit={disableMfa} className="space-y-4">
          <label className="form-control w-full max-w-sm" htmlFor="mfa-current-password">
            <span className="label-text mb-2">Current password</span>
            <input
              id="mfa-current-password"
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              data-sentry-mask
              value={password}
              onChange={event => setPassword(event.target.value)}
              className="input input-bordered w-full"
            />
          </label>
          <CodeInput id="mfa-current-code" label="Authenticator code" value={code} onChange={setCode} />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="btn btn-error btn-sm">
              {busy ? 'Disabling...' : 'Disable MFA'}
            </button>
            <button type="button" onClick={cancel} disabled={busy} className="btn btn-ghost btn-sm">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MfaRoute;
